import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSendChatMessage, usePageContext } from '../../api';
import { useCustomerIntelligence } from '../../contexts/CustomerIntelligenceContext';
import { useProactiveEngagement } from '../../hooks/useProactiveEngagement';
import { ProactiveMessage } from './ProactiveMessage';
import type { Product } from '@petmas/shared';
import { UI_CONSTANTS } from '@petmas/shared';
import { formatPrice } from '../../utils/format';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  products?: Product[];
  timestamp: Date;
}

export function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const params = useParams<{ id?: string }>();

  const { mutate: sendMessage, isPending } = useSendChatMessage();
  const { getAIContextPayload } = useCustomerIntelligence();

  // Get page-aware context for welcome message and quick actions
  const { data: pageContext } = usePageContext(location.pathname, params.id);

  // Proactive engagement - shows subtle prompts at optimal moments
  const {
    shouldShow: showProactive,
    message: proactiveMessage,
    triggerId: proactiveTriggerId,
    dismiss: dismissProactive,
    engage: engageProactive,
  } = useProactiveEngagement(messages.length);

  // Handle proactive message engagement (opens chat with the proactive message)
  const handleProactiveEngage = useCallback(() => {
    engageProactive();
    setIsOpen(true);
    // Optionally pre-populate the chat with the proactive context
    if (proactiveMessage) {
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: proactiveMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [engageProactive, proactiveMessage]);

  // Handle proactive message dismissal
  const handleProactiveDismiss = useCallback((triggerId: string) => {
    dismissProactive(triggerId);
  }, [dismissProactive]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Add welcome message on first open (page-aware)
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = pageContext?.welcomeMessage ??
        '¡Hola! Soy el asistente de PetMas. ¿En que puedo ayudarte hoy? Puedo ayudarte a encontrar productos para tu mascota o resolver cualquier duda que tengas.';

      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length, pageContext?.welcomeMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isPending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');

    // Get full customer intelligence context for AI personalization
    const aiContext = getAIContextPayload();

    sendMessage(
      { message: input.trim(), conversationId, context: aiContext },
      {
        onSuccess: (data) => {
          setConversationId(data.conversationId);
          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: data.response,
            products: data.products,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, assistantMessage]);
        },
        onError: () => {
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        },
      }
    );
  };

  const handleQuickAction = (text: string) => {
    setInput(text);
    inputRef.current?.focus();
  };

  return (
    <>
      {/* Proactive AI message - shown when AI wants to engage */}
      {showProactive && proactiveMessage && proactiveTriggerId && !isOpen && (
        <ProactiveMessage
          message={proactiveMessage}
          triggerId={proactiveTriggerId}
          onEngage={handleProactiveEngage}
          onDismiss={handleProactiveDismiss}
          autoHideDelay={15000}
        />
      )}

      {/* Chat button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary-dark transition-all ${
          isOpen || showProactive ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        aria-label="Abrir chat"
      >
        <ChatIcon className="w-6 h-6" />
      </button>

      {/* Chat window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden transition-all ${
          isOpen
            ? 'scale-100 opacity-100 pointer-events-auto'
            : 'scale-95 opacity-0 pointer-events-none'
        }`}
        style={{ maxHeight: 'calc(100vh - 120px)' }}
      >
        {/* Header */}
        <div className="bg-primary text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <BotIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold">Asistente PetMas</h3>
              <p className="text-xs text-primary-light/80">Siempre disponible</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-white/20 rounded"
            aria-label="Cerrar chat"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] ${
                  message.role === 'user'
                    ? 'bg-primary text-white rounded-2xl rounded-tr-sm'
                    : 'bg-white border rounded-2xl rounded-tl-sm shadow-sm'
                } p-3`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                {/* Product cards */}
                {message.products && message.products.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {message.products.slice(0, UI_CONSTANTS.MAX_SUGGESTIONS_LIMIT).map((product) => (
                      <ChatProductCard key={product.id} product={product} />
                    ))}
                    {message.products.length > UI_CONSTANTS.MAX_SUGGESTIONS_LIMIT && (
                      <Link
                        to={`/buscar?ai=true`}
                        className="block text-center text-sm text-primary hover:text-primary-dark"
                      >
                        Ver {message.products.length - UI_CONSTANTS.MAX_SUGGESTIONS_LIMIT} productos mas
                      </Link>
                    )}
                  </div>
                )}

                <p className="text-[10px] mt-1 opacity-60">
                  {message.timestamp.toLocaleTimeString('es-PE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isPending && (
            <div className="flex justify-start">
              <div className="bg-white border rounded-2xl rounded-tl-sm shadow-sm p-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.1s]" />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick actions (page-aware) */}
        {messages.length <= 1 && (
          <div className="px-4 py-2 border-t bg-white">
            <p className="text-xs text-gray-500 mb-2">
              {pageContext?.pageType === 'product' ? 'Preguntas sobre este producto:' : 'Preguntas frecuentes:'}
            </p>
            <div className="flex flex-wrap gap-2">
              {(pageContext?.quickActions ?? [
                'Mi perro tiene piel seca',
                'Comida para cachorro',
                'Productos para gatos',
              ]).map((action) => (
                <QuickAction key={action} onClick={handleQuickAction}>
                  {action}
                </QuickAction>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu mensaje..."
              disabled={isPending}
              className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <button
              type="submit"
              disabled={!input.trim() || isPending}
              className="p-2 bg-primary text-white rounded-full hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SendIcon className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function ChatProductCard({ product }: { product: Product }) {
  return (
    <Link
      to={`/producto/${product.id}`}
      className="flex gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <div className="w-12 h-12 bg-gray-200 rounded flex-shrink-0 overflow-hidden">
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
        <p className="text-sm font-semibold text-primary">
          {formatPrice(product.price)}
        </p>
      </div>
    </Link>
  );
}

function QuickAction({
  children,
  onClick,
}: {
  children: string;
  onClick: (text: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(children)}
      className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
    >
      {children}
    </button>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function BotIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 011 1v3a1 1 0 01-1 1h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2a1 1 0 01-1-1v-3a1 1 0 011-1h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2zM7.5 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm9 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM12 9c-2.76 0-5 2.24-5 5v4h10v-4c0-2.76-2.24-5-5-5z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}
